/**
 * snapshotBeforeOp — Sprint 3 (F5E-003)
 *
 * Exporta documentos a la colección `backups_auto` antes de una operación
 * destructiva o masiva. Permite restauración ad-hoc desde la UI de admin.
 *
 * Estructura:
 *   backups_auto/{backup_id}
 *     ├─ operation: string
 *     ├─ timestamp: Timestamp
 *     ├─ userId, userEmail, userRol
 *     ├─ collections: string[]
 *     ├─ docCount: number
 *     └─ docs (subcolección): { collection, docId, data }
 *
 * Limitación: Firestore cobra por read; no usar con >10k docs sin paginar.
 * Para backups grandes, preferir Cloud Scheduler + Firestore Export a GCS.
 */

import {
  collection,
  doc,
  getDocs,
  writeBatch,
  serverTimestamp,
  addDoc,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { logAudit } from './auditLog';

export interface SnapshotOptions {
  operation: string;
  userId?: string;
  userEmail?: string;
  userRol?: string;
  /** Si true, la operación NO procede si falla el snapshot. Default true. */
  requireSuccess?: boolean;
  metadata?: Record<string, any>;
}

export interface DocRef {
  collection: string;
  docId: string;
}

/**
 * Crea un snapshot de los documentos especificados. Retorna el ID del backup.
 * Lanza error si requireSuccess=true y el snapshot falla.
 */
export async function snapshotDocs(
  docsToBackup: DocRef[],
  options: SnapshotOptions
): Promise<string> {
  const requireSuccess = options.requireSuccess ?? true;

  try {
    // Crear documento padre del backup
    const backupRef = await addDoc(collection(db, 'backups_auto'), {
      operation: options.operation,
      timestamp: serverTimestamp(),
      userId: options.userId || null,
      userEmail: options.userEmail || null,
      userRol: options.userRol || null,
      collections: Array.from(new Set(docsToBackup.map((d) => d.collection))),
      docCount: docsToBackup.length,
      metadata: options.metadata || {},
      status: 'pending',
    });

    // Leer contenido de cada doc y guardar en subcolección
    // Procesamos en chunks de 400 para respetar límite Firestore batch
    const chunkSize = 400;
    for (let i = 0; i < docsToBackup.length; i += chunkSize) {
      const chunk = docsToBackup.slice(i, i + chunkSize);
      const batch = writeBatch(db);
      for (const { collection: col, docId } of chunk) {
        const snap = await getDocs(collection(db, col));
        const docData = snap.docs.find((d) => d.id === docId)?.data();
        if (docData) {
          const snapDocRef = doc(db, 'backups_auto', backupRef.id, 'docs', `${col}_${docId}`);
          batch.set(snapDocRef, {
            collection: col,
            docId,
            data: docData,
            savedAt: serverTimestamp(),
          });
        }
      }
      await batch.commit();
    }

    // Marcar backup como completo
    const { updateDoc } = await import('firebase/firestore');
    await updateDoc(backupRef, { status: 'completed' });

    await logAudit({
      action: 'export',
      collection: 'backups_auto',
      docId: backupRef.id,
      userId: options.userId,
      userEmail: options.userEmail,
      userRol: options.userRol,
      metadata: {
        operation: options.operation,
        docCount: docsToBackup.length,
      },
    });

    return backupRef.id;
  } catch (e: any) {
    if (process.env.NODE_ENV === 'development') console.error('[snapshotDocs]', e);
    if (requireSuccess) {
      throw new Error(`Backup previo falló: ${e?.message || e}. Operación abortada.`);
    }
    return '';
  }
}

/**
 * Atajo para operaciones que trabajan sobre una colección completa filtrada.
 * Lee los docs que serán afectados y los respalda.
 */
export async function snapshotCollectionDocs(
  collectionName: string,
  docIds: string[],
  options: SnapshotOptions
): Promise<string> {
  const refs = docIds.map((id) => ({ collection: collectionName, docId: id }));
  return snapshotDocs(refs, options);
}
